import { lazy, Suspense, useEffect } from "react";
import { Routes, Route, Link, useParams } from "react-router-dom";
import CookieConsent, { getCookieConsentValue } from "react-cookie-consent";
import { useTranslation } from "react-i18next";
import RootRedirect from "./routes/RootRedirect.jsx";
import ContentRedirect from "./routes/ContentRedirect.jsx";
import LanguageLayout from "./routes/LanguageLayout.jsx";
import ScrollToTop from "./routes/ScrollToTop.jsx";
import OrganizationJsonLd from "./components/shared/OrganizationJsonLd.jsx";
import { initGA } from "./utils/analytics.js";

const DATENSCHUTZ_PATH = { de: "datenschutz", en: "privacy" };

const cookieBannerStyle = {
  position: "fixed",
  bottom: 0,
  left: 0,
  width: "100%",
  zIndex: 999,
  background: "var(--color-bg-raised)",
  borderTop: "1px solid rgba(206,149,50,0.35)",
  boxShadow: "0 -8px 24px rgba(0,0,0,0.35)",
  padding: "20px 32px",
  display: "flex",
  alignItems: "center",
  gap: "24px",
  flexWrap: "wrap",
  fontFamily: "inherit",
};

const cookieContentStyle = {
  flex: "1 1 320px",
  margin: 0,
  fontSize: "14px",
  lineHeight: 1.6,
  color: "var(--color-cream-soft)",
};

const cookieAcceptStyle = {
  background: "var(--color-gold)",
  color: "var(--color-ink)",
  border: "none",
  borderRadius: "var(--radius-pill)",
  padding: "12px 26px",
  fontSize: "14px",
  fontWeight: 500,
  cursor: "pointer",
  margin: 0,
};

const cookieDeclineStyle = {
  background: "transparent",
  color: "var(--color-cream)",
  border: "1px solid rgba(254,250,239,0.4)",
  borderRadius: "var(--radius-pill)",
  padding: "12px 26px",
  fontSize: "14px",
  fontWeight: 500,
  cursor: "pointer",
  margin: 0,
};

const Home = lazy(() => import("./pages/Home.jsx"));
const EventList = lazy(() => import("./pages/Events/EventList.jsx"));
const EventDetail = lazy(() => import("./pages/Events/EventDetail.jsx"));
const LarpHub = lazy(() => import("./pages/Larp/LarpHub.jsx"));
const LarpSubpage = lazy(() => import("./pages/Larp/LarpSubpage.jsx"));
const Impressum = lazy(() => import("./pages/Legal/Impressum.jsx"));
const Datenschutz = lazy(() => import("./pages/Legal/Datenschutz.jsx"));
const AGB = lazy(() => import("./pages/Legal/AGB.jsx"));
const Kontakt = lazy(() => import("./pages/Kontakt.jsx"));
const Newsletter = lazy(() => import("./pages/Newsletter.jsx"));
const DonationOverview = lazy(() => import("./pages/Spenden/DonationOverview.jsx"));
const DonationDetail = lazy(() => import("./pages/Spenden/DonationDetail.jsx"));
const AboutHub = lazy(() => import("./pages/About/AboutHub.jsx"));
const AboutSubpage = lazy(() => import("./pages/About/AboutSubpage.jsx"));
const OrgasPage = lazy(() => import("./pages/About/OrgasPage.jsx"));
const SupportSubpage = lazy(() => import("./pages/Support/SupportSubpage.jsx"));
const Press = lazy(() => import("./pages/Press.jsx"));
const Info = lazy(() => import("./pages/Info.jsx"));
const NotFound = lazy(() => import("./pages/NotFound.jsx"));

function WithLang({ Component }) {
  const { lang } = useParams();
  return <Component lang={lang} />;
}

function WithLangTopic({ Component, topic }) {
  const { lang } = useParams();
  return <Component lang={lang} topic={topic} />;
}

export default function App() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === "en" ? "en" : "de";

  useEffect(() => {
    if (getCookieConsentValue() === "true") {
      initGA();
    }
  }, []);

  return (
    <>
      <ScrollToTop />
      <OrganizationJsonLd />
      <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/go/:id" element={<ContentRedirect />} />
          <Route path="/:lang" element={<LanguageLayout />}>
            <Route index element={<WithLang Component={Home} />} />
            <Route path="events" element={<WithLang Component={EventList} />} />
            <Route path="events/:slug" element={<WithLang Component={EventDetail} />} />
            <Route path="larp-erleben" element={<WithLang Component={LarpHub} />} />
            <Route path="experience-larp" element={<WithLang Component={LarpHub} />} />
            <Route path="larp-erleben/:slug" element={<WithLang Component={LarpSubpage} />} />
            <Route path="experience-larp/:slug" element={<WithLang Component={LarpSubpage} />} />
            <Route path="impressum" element={<WithLang Component={Impressum} />} />
            <Route path="legal-notice" element={<WithLang Component={Impressum} />} />
            <Route path="datenschutz" element={<WithLang Component={Datenschutz} />} />
            <Route path="privacy" element={<WithLang Component={Datenschutz} />} />
            <Route path="agb" element={<WithLang Component={AGB} />} />
            <Route path="terms" element={<WithLang Component={AGB} />} />
            <Route path="kontakt" element={<WithLang Component={Kontakt} />} />
            <Route path="contact" element={<WithLang Component={Kontakt} />} />
            <Route path="newsletter" element={<WithLang Component={Newsletter} />} />
            <Route path="spenden" element={<WithLang Component={DonationOverview} />} />
            <Route path="donate" element={<WithLang Component={DonationOverview} />} />
            <Route path="spenden/:slug" element={<WithLang Component={DonationDetail} />} />
            <Route path="donate/:slug" element={<WithLang Component={DonationDetail} />} />
            <Route path="ueber-uns" element={<WithLang Component={AboutHub} />} />
            <Route path="about" element={<WithLang Component={AboutHub} />} />
            <Route path="ueber-uns/team" element={<WithLangTopic Component={AboutSubpage} topic="team" />} />
            <Route path="about/team" element={<WithLangTopic Component={AboutSubpage} topic="team" />} />
            <Route path="ueber-uns/gesellschaftsvertrag" element={<WithLangTopic Component={AboutSubpage} topic="charter" />} />
            <Route path="about/charter" element={<WithLangTopic Component={AboutSubpage} topic="charter" />} />
            <Route path="ueber-uns/rueckblick" element={<WithLangTopic Component={AboutSubpage} topic="history" />} />
            <Route path="about/history" element={<WithLangTopic Component={AboutSubpage} topic="history" />} />
            <Route path="ueber-uns/rechenschaftsberichte" element={<WithLangTopic Component={AboutSubpage} topic="reports" />} />
            <Route path="about/accountability-reports" element={<WithLangTopic Component={AboutSubpage} topic="reports" />} />
            <Route path="ueber-uns/verhaltenskodex" element={<WithLangTopic Component={AboutSubpage} topic="conduct" />} />
            <Route path="about/code-of-conduct" element={<WithLangTopic Component={AboutSubpage} topic="conduct" />} />
            <Route path="ueber-uns/fuer-orgas" element={<WithLang Component={OrgasPage} />} />
            <Route path="about/for-organizers" element={<WithLang Component={OrgasPage} />} />
            <Route
              path="unterstuetzen/foerdermitgliedschaft"
              element={<WithLangTopic Component={SupportSubpage} topic="membership" />}
            />
            <Route path="support/membership" element={<WithLangTopic Component={SupportSubpage} topic="membership" />} />
            <Route
              path="unterstuetzen/sponsoring"
              element={<WithLangTopic Component={SupportSubpage} topic="sponsoring" />}
            />
            <Route path="support/sponsoring" element={<WithLangTopic Component={SupportSubpage} topic="sponsoring" />} />
            <Route path="unterstuetzen/ehrenamt" element={<WithLangTopic Component={SupportSubpage} topic="volunteer" />} />
            <Route path="support/volunteer" element={<WithLangTopic Component={SupportSubpage} topic="volunteer" />} />
            <Route path="presse" element={<WithLang Component={Press} />} />
            <Route path="press" element={<WithLang Component={Press} />} />
            <Route path="infos" element={<WithLang Component={Info} />} />
            <Route path="info" element={<WithLang Component={Info} />} />
            <Route path="*" element={<WithLang Component={NotFound} />} />
          </Route>
        </Routes>
      </Suspense>

      <div role="region" aria-label={t("cookieConsent.regionLabel")}>
        <CookieConsent
          enableDeclineButton
          buttonText={t("cookieConsent.accept")}
          declineButtonText={t("cookieConsent.decline")}
          style={cookieBannerStyle}
          contentStyle={cookieContentStyle}
          buttonStyle={cookieAcceptStyle}
          declineButtonStyle={cookieDeclineStyle}
          customButtonWrapperAttributes={{ style: { display: "flex", gap: "12px", flexWrap: "wrap", flex: "0 0 auto" } }}
          onAccept={() => initGA()}
        >
          {t("cookieConsent.message")}{" "}
          <Link to={`/${lang}/${DATENSCHUTZ_PATH[lang]}`} style={{ color: "var(--color-gold)", textDecoration: "underline" }}>
            {t("cookieConsent.privacyLink")}
          </Link>
        </CookieConsent>
      </div>
    </>
  );
}
